import type { WithContext, BreadcrumbList, ItemList, WebPage } from 'schema-dts';
import type { ComputedRef } from 'vue';

interface BreadcrumbItem {
    name: string;
    url: string;
}

interface ItemListItem {
    name: string;
    url: string;
    description?: string;
    image?: string;
    jobTitle?: string;
}

interface PageData {
    name: string;
    description?: string;
    url: string;
}

interface UseJsonLdSchemaProps {
    breadcrumbs?: ComputedRef<BreadcrumbItem[]> | BreadcrumbItem[];
    items?: ComputedRef<ItemListItem[]> | ItemListItem[];
    pageData?: ComputedRef<PageData> | PageData;
    homeUrl?: string;
}

export const useJsonLdSchema = ({
    breadcrumbs,
    items: itemList,
    pageData,
    homeUrl = '/'
}: UseJsonLdSchemaProps) => {
    const jsonLd = computed(() => {
        const schemas = [];

        // Добавляем BreadcrumbList если есть breadcrumbs
        const breadcrumbItems = unref(breadcrumbs);
        let hasHome = false;
        let breadcrumbList: BreadcrumbItem[] = [];

        if (breadcrumbItems?.length) {
            // Проверяем, начинается ли breadcrumb с Home
            hasHome = breadcrumbItems[0]?.name.toLowerCase() === 'home';
            breadcrumbList = hasHome ? breadcrumbItems : [
                { name: 'Home', url: homeUrl },
                ...breadcrumbItems
            ];

            schemas.push({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbList.map((item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@id': item.url,
                        name: item.name
                    }
                }))
            } as WithContext<BreadcrumbList>);
        }

        // Добавляем ItemList если есть items
        const items = unref(itemList);
        if (items?.length) {
            schemas.push({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                itemListElement: items.map((item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'Person',
                        '@id': item.url,
                        name: item.name,
                        description: item.description,
                        image: item.image,
                        jobTitle: item.jobTitle
                    }
                }))
            } as WithContext<ItemList>);
        }

        // Добавляем WebPage если есть pageData
        const page = unref(pageData);
        if (page) {
            schemas.push({
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                '@id': page.url,
                name: page.name,
                description: page.description,
                ...(breadcrumbItems?.length && {
                    breadcrumb: {
                        '@type': 'BreadcrumbList',
                        itemListElement: (hasHome ? breadcrumbItems : [
                            { name: 'Home', url: homeUrl },
                            ...breadcrumbItems
                        ]).map((item, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            item: {
                                '@id': item.url,
                                name: item.name
                            }
                        }))
                    }
                })
            } as WithContext<WebPage>);
        }

        return schemas;
    });

    return {
        jsonLd
    };
}; 